import { Carousel } from '@elipssolution/harfang';
import { useTheme } from '@mui/material';
import { useCallback, useMemo } from 'react';

import { ExternalApplicationDetails } from './types/externalApplication';
import { getAppDetails } from './utils/appDetailsUtil';
import { WidgetExternalApplicationsContext } from './utils/context';
import useFormat from '../../../hooks/useFormat';
import { WidgetComponentProps, WidgetExternalApplicationsConfigurationType } from '../../../types/widget';
import { measureTextWidth } from '../../../utils/measureTextWidth';
import { RenderModeEnum } from '../../../utils/widgets';
import { useSession } from '../../components/session/SessionContext';
import useCustomRouter from '../../hooks/useCustomRouter';
import { UserTypeEnum } from '../../types/user';

const widgetContextValue = { renderMode: RenderModeEnum.CAROUSEL };

const WidgetExternalApplications = ({
	configuration,
}: WidgetComponentProps<WidgetExternalApplicationsConfigurationType>) => {
	const { query } = useCustomRouter();
	const { typography } = useTheme();
	const { isMobileFormat } = useFormat();
	const {
		user: { type: userType },
	} = useSession();

	const isInSettingsDialog = query.settings;

	const itemWidth = isMobileFormat ? 70 : 132;

	const carouselItems = useMemo(
		() =>
			(configuration?.applications ?? [])
				.sort((a, b) => a.name.localeCompare(b.name))
				.reduce((acc, application) => {
					const { name, specific } = application;

					return (userType === UserTypeEnum.INTERNAL && !application.isInternal) ||
						(userType === UserTypeEnum.EXTERNAL && !application.isExternal) ||
						(isMobileFormat && !application.isAvailableOnMobileFormat) ||
						(!isMobileFormat && !application.isAvailableOnDesktopFormat)
						? acc
						: ([
								...acc,
								getAppDetails(application)?.({
									name,
									specific,
								}),
						  ] as ExternalApplicationDetails[]);
				}, [] as ExternalApplicationDetails[]),
		[configuration?.applications, isMobileFormat, userType],
	);

	const hasApplicationNamesOnTwoLines = useMemo(
		() =>
			(configuration?.applications || []).some(
				({ name }) =>
					measureTextWidth({
						text: name,
						fontSize: typography.body1.fontSize as string,
						fontFamily: typography.body1.fontFamily as string,
					}) >=
					itemWidth - 8 - 8,
			),
		[configuration?.applications, itemWidth, typography.body1.fontFamily, typography.body1.fontSize],
	);

	const itemHeight = useMemo(() => {
		if (!isMobileFormat) {
			return 132;
		}

		return hasApplicationNamesOnTwoLines ? 120 : 96;
	}, [hasApplicationNamesOnTwoLines, isMobileFormat]);

	const dataSource = useCallback(
		() =>
			Promise.resolve({
				items: carouselItems,
				count: carouselItems.length,
			}),
		[carouselItems],
	);

	const renderItem = ({ component }: ExternalApplicationDetails) => component ?? <div />;

	const handleItemClick = ({ url }: ExternalApplicationDetails) => !isInSettingsDialog && window.open(url, '_blank');

	return (
		<WidgetExternalApplicationsContext.Provider value={widgetContextValue}>
			<Carousel<ExternalApplicationDetails>
				dataSource={dataSource}
				itemWidth={itemWidth}
				itemHeight={itemHeight}
				renderItem={renderItem}
				onItemClick={handleItemClick}
				isItemDisabled={({ isDisabled = false }: ExternalApplicationDetails) => isDisabled}
			/>
		</WidgetExternalApplicationsContext.Provider>
	);
};

export default WidgetExternalApplications;
